import React from "react";
import { Link as RouterLink } from "react-router-dom";
import posIcon from "../../images/pos-agent-banking.svg";
import bankIcon from "../../images/sme-business-accout.svg";
import loanIcon from "../../images/working-capital-loan.svg";

const geist = { fontFamily: "'Geist', sans-serif" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };

const OPEN_ACCOUNT_URL = "https://zikoramfb.qoreonline.com/onboarding/type";

const ArrowIcon = ({ color }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M3.75 9h10.5M10 4.75 14.25 9 10 13.25"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CARDS = [
  {
    icon: posIcon,
    title: "POS & Agent Banking",
    body: "Accept payments easily and serve customers faster with reliable POS and agency banking solutions.",
    cta: "Get Started",
    to: "/agency-banking",
    variant: "light",
  },
  {
    icon: bankIcon,
    title: "SME Business Account",
    body: "A business account designed to help you manage payments, operations, and everyday transactions with ease.",
    cta: "Open Account",
    href: OPEN_ACCOUNT_URL,
    variant: "mint",
  },
  {
    icon: loanIcon,
    title: "Working Capital Loan",
    body: "Access funds to restock inventory, manage cash flow, pay suppliers, or handle urgent business needs.",
    cta: "Apply Now",
    to: "/loans",
    variant: "dark",
  },
];

const VARIANTS = {
  light: {
    cardBg: "#FFFFFF",
    cardBorder: "1px solid #E6EAE2",
    chipBg: "#F1F4EF",
    title: "#132A22",
    body: "#5A6B60",
    link: "#295B4A",
  },
  mint: {
    cardBg: "#E8ECE3",
    cardBorder: "1px solid #DCE3D6",
    chipBg: "#FFFFFF",
    title: "#132A22",
    body: "#566256",
    link: "#295B4A",
  },
  dark: {
    cardBg: "#132A22",
    cardBorder: "1px solid #132A22",
    chipBg: "rgba(134, 199, 168, 0.14)",
    title: "#86C7A8",
    body: "#C9D6CE",
    link: "#86C7A8",
  },
};

const ProductCard = ({ card }) => {
  const v = VARIANTS[card.variant];
  const linkProps = card.href
    ? {
        component: "a",
        href: card.href,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : { component: RouterLink, to: card.to };

  const linkInner = (
    <>
      {card.cta}
      <ArrowIcon color={v.link} />
    </>
  );

  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: 0,
        backgroundColor: v.cardBg,
        border: v.cardBorder,
        borderRadius: "16px",
        padding: "clamp(24px, 2.2vw, 32px)",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "48px",
          height: "48px",
          borderRadius: "12px",
          backgroundColor: v.chipBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <img
          src={card.icon}
          alt=""
          aria-hidden="true"
          style={{ width: "24px", height: "24px", display: "block" }}
        />
      </div>

      <h3
        style={{
          ...geist,
          fontWeight: 600,
          fontSize: "clamp(18px, 1.53vw, 22px)",
          lineHeight: 1.25,
          color: v.title,
          margin: 0,
        }}
      >
        {card.title}
      </h3>

      <p
        style={{
          ...geist,
          fontWeight: 400,
          fontSize: "clamp(13px, 1.04vw, 15px)",
          lineHeight: 1.65,
          color: v.body,
          margin: 0,
          flex: "1 1 auto",
        }}
      >
        {card.body}
      </p>

      {card.href ? (
        <a
          {...linkProps}
          style={{
            ...geist,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: 600,
            fontSize: "clamp(13px, 1.04vw, 15px)",
            color: v.link,
            textDecoration: "none",
          }}
        >
          {linkInner}
        </a>
      ) : (
        <RouterLink
          to={card.to}
          style={{
            ...geist,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: 600,
            fontSize: "clamp(13px, 1.04vw, 15px)",
            color: v.link,
            textDecoration: "none",
          }}
        >
          {linkInner}
        </RouterLink>
      )}
    </div>
  );
};

const SmeProducts = () => {
  return (
    <>
      <style>{`
        .sme-products-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: clamp(24px, 4vw, 80px);
        }
        .sme-products-grid {
          display: flex;
          gap: clamp(16px, 1.67vw, 24px);
          margin-top: clamp(36px, 4vw, 56px);
        }
        @media (max-width: 900px) {
          .sme-products-head { flex-direction: column !important; align-items: flex-start !important; }
          .sme-products-grid { flex-direction: column !important; }
        }
      `}</style>

      <section
        style={{
          width: "100%",
          backgroundColor: "transparent",
          boxSizing: "border-box",
          padding: "clamp(56px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px)",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <div className="sme-products-head">
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#66A681",
                    flexShrink: 0,
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    ...geistMono,
                    fontWeight: 500,
                    fontSize: "12px",
                    lineHeight: 1,
                    letterSpacing: "0.1em",
                    color: "#295B4A",
                    textTransform: "uppercase",
                  }}
                >
                  SME Products
                </span>
              </div>

              <h2
                style={{
                  ...instrumentSerif,
                  fontWeight: 400,
                  fontSize: "clamp(30px, 3.61vw, 52px)",
                  lineHeight: 1.08,
                  letterSpacing: "-0.01em",
                  color: "#132A22",
                  margin: 0,
                  maxWidth: "560px",
                }}
              >
                Everything your business needs to operate and grow.
              </h2>
            </div>

            <p
              style={{
                ...geist,
                fontWeight: 400,
                fontSize: "clamp(14px, 1.11vw, 16px)",
                lineHeight: 1.65,
                color: "#5E5E5E",
                margin: 0,
                maxWidth: "320px",
              }}
            >
              Flexible banking and business support designed around how SMEs
              actually work.
            </p>
          </div>

          <div className="sme-products-grid">
            {CARDS.map((card) => (
              <ProductCard key={card.title} card={card} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default SmeProducts;
